interface DevelopmentFlag {
  displayName: string;
  description: string;
  values: readonly string[];
}

/**
 * Development flags that can be toggled between different states for:
 * - continuously integrating hidden from users
 * - easily test complex states in the UI
 *
 * **When adding new flags:**
 *   please make sure the list is still sorted alphabetically.
 */
export const DEVELOPMENT_FLAGS = {
  'always-show-development-tools': {
    displayName: 'Always show development tools bubble',
    description:
      'Makes the development tools bubble visible even if no flags are enabled.',
    values: ['yes', 'no'],
  },
  'ui-theme-override': {
    displayName: 'UI theme override',
    description: 'Overrides the appearance setting to set light or dark mode.',
    values: ['light', 'dark'],
  },
  'console-tracking': {
    displayName: 'Console tracking',
    description: 'Logs tracking events to the console.',
    values: ['on'],
  },
  'engagement-limit-progress': {
    displayName: 'Engagement limit progress',
    description:
      'Sets the progress of the engagement limit to specific thresholds that triggers different UI elements to be shown.',
    values: ['unengaged', 'activated-limit', 'nearing-limit', 'reached-limit'],
  },
  'home-mode': {
    displayName: 'Home mode',
    description: 'Forces the user home page to a specific state.',
    values: ['not-created', 'created', 'activated'],
  },
  'locale-override': {
    displayName: 'Locale override',
    description: 'Overrides the set language for the application',
    values: ['en', 'de-DE', 'es-ES', 'pt-BR'],
  },
  'mask-translated-messages': {
    displayName: 'Mask i18n messages 🎭',
    description: 'Overrides the translated messages with an emoji mask.',
    values: ['on', 'off'],
  },
  'quick-mask-translated-messages-toggle': {
    displayName: 'Mask i18n Quick toggle 🎭',
    description: 'Add masking toggle to dev tools bubble.',
    values: ['toggle', 'hover', 'off'],
  },
  'template-helpers': {
    displayName: 'Template Helpers',
    description: 'Show extra helpers and info about templates.',
    values: ['on'],
  },
  'test-votes-indicator': {
    displayName: 'Test votes indicator',
    description: 'Shows a test votes indicator on the slide preview.',
    values: ['on'],
  },
  'notification-fixtures': {
    displayName: 'Notification fixtures',
    description: 'Toggle a predefined set of notifications for testing UI.',
    values: ['loading-state', 'error-state', 'empty-state'],
  },
  'onboarding-navigation': {
    displayName: 'Onboarding navigation',
    description: 'Allows navigating back between onboarding steps.',
    values: ['on'],
  },
  'checklist-devtools': {
    displayName: 'Checklist DevTools',
    description: 'allows to manipulate the checklist state in the editor.',
    values: ['on'],
  },
  'monthly-billing': {
    displayName: 'Monthly billing toggle',
    description: 'Enables possibility to select monthly billing.',
    values: ['on'],
  },
  'boolean-entitlements-reason-override': {
    displayName: 'Entitlements reason override (boolean)',
    description:
      'Overrides all boolean entitlements for the application to be false with a certain reason.',
    values: ['not-in-plan', 'not-in-role'],
  },
  'braze-wipe-data': {
    displayName: 'Braze wipe data',
    description: 'Wipes Braze user data on initialization.',
    values: ['on'],
  },
  'disable-blab-tracking': {
    displayName: 'Disable "blab" tracking',
    description:
      'Disable API calls to "blab". The "tracking-api" is not available in Linkup, so these requests will always fail locally.',
    values: ['on'],
  },
  'pc-interactive-canvas-editing': {
    displayName: 'PC - Interactive canvas editing',
    description: 'Enables interactive canvas editing.',
    values: ['on', 'off'],
  },
} as const satisfies Record<string, DevelopmentFlag>;

export type DevelopmentFlagKey = keyof typeof DEVELOPMENT_FLAGS;
export type DevelopmentFlagValue<T extends DevelopmentFlagKey> =
  (typeof DEVELOPMENT_FLAGS)[T]['values'][number];
